<template>
	<teleport to="#toasts-overlay-teleport">
        <transition appear class="dont-close-side-modals" name="toast-animation" @after-leave="emit('hideMe')">
            <div v-if="showMe" class="toast-overlay-wrapper">
                <div class="toast-content-wrapper"
                :class="[
                    toastType,
                    {
                        'mobile': getMobileAppState ,
                        'ios-mobile': getDeviceInUse === 'ios',
                        'buttons-padding': buttonsParams && !!buttonsParams.length
                    }
                ]">
                    <!-- Messages -->
                    <div class="message">
                        <!-- Icon -->
                        <status-feedback-icon class="statusIcon"/>
                        <!-- Feedback Message -->
                        <div class="container-feedback-message">
                            <div
                            v-for="(message, index) in parsedFeedbackText"
                            class="feedback-message"
                            :key="`message${index}`"
                            v-html="message"/>
                        </div>
                    </div>
                    <!-- Buttons -->
                    <div v-if="buttonsParams && !!buttonsParams.length" class="buttons">
                        <buttons-tertiary
                        v-for="(button, index) in buttonsParams"
                        class="button"
                        buttonSize="small"
                        :key="index.key"
                        :label="button.text"
                        @clicked="buttonClickHandler(button)"/>
                    </div>
                </div>
            </div>
        </transition>
	</teleport>
</template>

<script setup>
import ButtonsTertiary from '@/components/generic/buttons/Tertiary'
/* Vue */
import { ref, computed, watch, onMounted, defineAsyncComponent } from 'vue'
import { iconModules } from '@/assets/icons/iconModules'

/* Store */
import { storeToRefs } from 'pinia'
import { useProjectBaseStore } from '@/store/projectBase'
import { useSessionStore } from '@/store/session'

const { getSessionExpiredState } = storeToRefs(useSessionStore())
const { getMobileAppState, getDeviceInUse } = storeToRefs(useProjectBaseStore())

/* Emmitters */
const emit = defineEmits(['hideMe'])

/** Props received
@feedbackText { String, Array }
@toastType { String } possible values [success, negative, warning]
@buttonsParams { Array } each item is an Object
	- text { String }
	- callbackAction { Function }
    - disabled { Boolean }
@triggerClose { Boolean } prop to trigger toast to hide itself so it does leave animation
*/
const props = defineProps({
    feedbackText: [String, Array],
    toastType: String,
    buttonsParams: Array,
    triggerClose: Boolean
})

const parsedFeedbackText = computed(() => {
    return Array.isArray(props.feedbackText) ? props.feedbackText : [props.feedbackText]
})

/* Computed for icon */
const statusFeedbackIcon = computed(() => {
    const icon = iconMapper[props.toastType.toLowerCase()]
	return defineAsyncComponent(iconModules[`/src/assets/icons/${icon}.svg`])
})

const iconMapper = {
    success: 'check-circle',
    info: 'check-circle',
    negative: 'error',
    error: 'error',
    warning: 'warning'
}

const buttonClickHandler = button => {
    showMe.value = false
    if (button.callbackAction) {
		setTimeout(() => {
			button.callbackAction()
		}, 100)
	}
}

watch(() => getSessionExpiredState.value, (newVal, oldVal) => {
    if (!newVal && !!props.buttonsParams.find(obj => obj.id === 'retryButton')) {
        showMe.value = false
        props.buttonsParams.find(obj => obj.id === 'retryButton').callbackAction()
    }
})

watch(() => props.triggerClose, (newVal, oldVal) => {
    if (props.triggerClose) showMe.value = false
})

/* Toast cycle */
const showMe = ref(false)
onMounted(() => {
    if (props.buttonsParams && !!props.buttonsParams.find(obj => obj.errorCode === 'expired_token')) return
    showMe.value = true

    /* If we have no buttons we will force toast to hide after 3s */
    if (!props.buttonsParams || !props.buttonsParams.length) {
        setTimeout(() => { showMe.value = false }, 3000)
    }
})
</script>

<style lang="scss" scoped>
.toast-overlay-wrapper {
    @include toastsOverlayTeleportStyles;
    width: 100%;

    display: flex;
    justify-content: center;

    transition: transform 0.8s linear;
    transform: translateY(0px);

    &.toast-animation-enter-active,
    &.toast-animation-leave-active {
        transform:translateY(0px);
    }

    &.toast-animation-enter-from,
    &.toast-animation-leave-to {
        transform:translateY(110%);
    }

    .toast-content-wrapper {
        width: fit-content;
        max-width: 920px;
        min-height: 56px;

        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: space-evenly;

        border-radius: 12px;
        padding: 8px 16px;
        margin-bottom: 16px;
        gap: 16px;

        @media only screen and (max-width: 800px) {
            max-width: calc(100% - 32px);
        }

        &.info, &.success {
            background-color: var(--positive);
        }

        &.negative, &.error {
            background-color: var(--negative);
        }

        &.warning, &.alert {
            background-color: var(--warning);
        }

        .message {
            display: flex;
            flex-direction: row;
            align-items: center;
            gap: 8px;

            .statusIcon {
                min-width: 16px;
                min-height: 16px;

                :deep(.change-color-fill) {
                    fill: var(--secondary-light-1);
                }
            }

            .container-feedback-message {
                display: flex;
                flex-direction: column;
                gap: 4px;

                .feedback-message {
                    @include H6-Regular;
                    line-height: 18px;
                    color: var(--secondary-light-4);
                }
            }
        }

        .buttons {
            display: flex;
            flex-direction: row;
            flex-shrink: 0;
            gap: 8px;
        }

        &.buttons-padding {
            padding-right: 8px;
        }

        // MOBILE STYLES
        &.mobile {
            flex-direction: column;
            align-items: flex-start;

            width: calc(100% - 16px);
            max-width: unset;
            margin-bottom: 8px;

            &.ios-mobile {
                padding-bottom: calc(constant(safe-area-inset-bottom)); /* iOS 11.0 */
                padding-bottom: calc(env(safe-area-inset-bottom)); /* iOS 11.2 */
            }

            .buttons {
                width: 100%;
                justify-content: flex-end;
            }
        }
    }
}
</style>
